import { useWebWorkerFn } from '@vueuse/core';
import { get } from 'lodash-es';
import {
  BUILTIN_LOCALE_KEYS,
  ContentType,
  expandLocaleObject,
  getRangePresets,
  LOCALE,
  type LocaleType,
  omit,
  pick,
  RequestMethod,
  ResultEnum,
} from '@mts-fe/utils';
import { cleanTranslationCache, t } from '@mts/hooks/web/useI18n';
import { i18n } from '@mts/locales/setupI18n';
import { SERVICE_PREFIX } from '@mts/utils';
import { getRequestHeaders } from '@mts/utils/auth';
import { globSetting } from '@mts/utils/env';
import { getLocaleStore } from '@/store';

const langApiServer = globSetting.apiUrl + SERVICE_PREFIX.LCP + '/multiLanguage';
/** 获取多语言服务接口地址 */
const getLocaleFetchUrl = () => {
  const { platform, device } = globSetting;
  const clientTypeList = ['GLOBAL', 'MENU', 'DICT', device];
  platform === 'LOWCODE' && clientTypeList.push('TERMINAL', 'DASHBOARD', 'BACKEND_MODEL');
  device === 'PC' && clientTypeList.push('PAD', 'PDA');
  const query = `?clientTypeList=${clientTypeList.join(',')}&includeOldKey=${device !== 'PAD'}`;
  return langApiServer + '/getByLang' + query;
};

/** 加载服务端语言集 */
export const loadLocaleMsg = async (locale: LocaleType = LOCALE.zh_CN) => {
  const res = await fetch(getLocaleFetchUrl(), {
    method: 'GET',
    headers: { ...getRequestHeaders(), 'metas-lang': locale },
  }).then((res) => res.json());

  setTimeout(() => {
    const hasSuccess = res.code === ResultEnum.SUCCESS && res.data;
    if (hasSuccess) {
      i18n?.global.mergeLocaleMessage(locale, expandLocaleObject(res.data.languagePackage));
      cacheLocaleMsg(locale);
    }
  }, 30);
};

/** 异步加载服务端语言集 */
export const openLocaleWorker = async (locale: LocaleType = getLocaleStore().getLocale) => {
  const { workerFn, workerTerminate } = useWebWorkerFn<(params: Recordable) => Promise<Recordable>>(
    async ({ url, options = {} }) => {
      const res = await fetch(url, options).then((res) => res.json());
      const data = res.code === 0 && res.data ? res.data.languagePackage : {};
      return expandLocaleObject(data);
    },
    { timeout: 6e5, localDependencies: [expandLocaleObject] },
  );

  try {
    const res = await workerFn({
      url: window.origin + getLocaleFetchUrl(),
      options: {
        method: 'GET',
        headers: { ...getRequestHeaders(), 'metas-lang': locale },
      },
    });
    if (!Object.keys(res).length) return;

    i18n?.global.mergeLocaleMessage(locale, res);
    console.debug('🛫 多语言包预抓取完成', locale);
    cacheLocaleMsg(locale);
  } finally {
    workerTerminate();
  }
};

const cacheLocaleMsg = (locale: LocaleType) => {
  const messages = omit(i18n?.global.getLocaleMessage(locale), BUILTIN_LOCALE_KEYS);
  localStorage.setItem('mts-language', JSON.stringify(messages));
  // mtsEmitter.emit('localeChanged', locale);

  // warmup
  requestIdleCallback(() => getRangePresets(t));
};

/** 更新本地多语言翻译 */
export function mergeLocaleMessage(langObj: { lanKey: string } & Recordable) {
  cleanTranslationCache(langObj.lanKey);

  const localeStore = getLocaleStore();
  const fieldKey = localeStore.getLocaleConfig()?.fieldKey;
  if (!fieldKey) return;
  i18n?.global.mergeLocaleMessage(
    localeStore.getLocale,
    expandLocaleObject({ [langObj.lanKey]: langObj[fieldKey] }),
  );
}

/** 获取指定key的所有翻译内容 */
export const getTranslations = async (key: string): Promise<Recordable<string>> => {
  const localeList = getLocaleStore().getLocaleList;
  if (!BUILTIN_LOCALE_KEYS.includes(key.split('.')[0])) {
    const url = langApiServer + '/listValueByLanKey';
    const res = await fetch(url, {
      method: RequestMethod.POST,
      headers: { 'Content-Type': ContentType.JSON, ...getRequestHeaders({ url }) },
      body: JSON.stringify({ key }),
    }).then((res) => res.json());

    return pick(
      res.data,
      localeList.map((i) => i.fieldKey),
    );
  }

  const res = {};
  for (const locale of localeList) {
    const message = (await import(`./lang/${locale.value}.ts`)).default?.message ?? {};
    res[locale.fieldKey] = get(message, key);
  }
  return res;
};
